import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";
import EmptyState from "../../common/EmptyState";

const STATUS_COLORS = {
  open: "error",
  answered: "info",
  closed: "default",
};

const PRIORITY_COLORS = {
  low: "success",
  medium: "warning",
  high: "error",
};

const SupportTickets = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();

  const [tickets, setTickets] = useState([]);
  const [view, setView] = useState(null); // { ticket, messages }
  const [createOpen, setCreateOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  // Create form
  const [form, setForm] = useState({
    subject: "",
    message: "",
    priority: "medium",
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    const res = await hitAxios({
      path: "/api/tickets/my-tickets",
      showSnackbar: false,
    });
    if (res?.data?.success) setTickets(res.data.data);
  };

  const openTicket = async (ticket) => {
    const res = await hitAxios({
      path: `/api/tickets/get/${ticket.uid}`,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setView({ ticket: res.data.ticket, messages: res.data.messages });
      setReplyText("");
    }
  };

  const createTicket = async () => {
    if (!form.subject.trim() || !form.message.trim()) return;
    setCreating(true);
    const res = await hitAxios({
      path: "/api/tickets/create",
      post: true,
      obj: form,
    });
    setCreating(false);
    if (res?.data?.success) {
      setCreateOpen(false);
      setForm({ subject: "", message: "", priority: "medium" });
      loadTickets();
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !view) return;
    setReplying(true);
    const res = await hitAxios({
      path: "/api/tickets/reply",
      post: true,
      obj: { ticket_uid: view.ticket.uid, message: replyText },
    });
    setReplying(false);
    if (res?.data?.success) {
      setReplyText("");
      await openTicket(view.ticket);
      loadTickets();
    }
  };

  const closeTicket = async () => {
    if (!window.confirm("Close this ticket?")) return;
    await hitAxios({
      path: "/api/tickets/close",
      post: true,
      obj: { ticket_uid: view.ticket.uid },
    });
    await openTicket(view.ticket);
    loadTickets();
  };

  const formatDate = (d) => new Date(d).toLocaleString();

  // ── Ticket Detail View ──────────────────────────────────────────────────
  if (view) {
    return (
      <Box>
        <PageHeader
          title={
            <Stack direction="row" spacing={1.5} alignItems="center">
              <IconButton size="small" onClick={() => setView(null)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="h5"
                fontWeight={700}
                noWrap
                sx={{ maxWidth: 400 }}
              >
                {view.ticket.subject}
              </Typography>
            </Stack>
          }
          subtitle={`Ticket #${view.ticket.uid?.substring(0, 8)} · ${formatDate(view.ticket.createdAt)}`}
          icon={SupportAgentIcon}
          primaryAction={
            view.ticket.status !== "closed" && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CloseIcon />}
                onClick={closeTicket}
                sx={{ borderRadius: 2 }}
              >
                Close Ticket
              </Button>
            )
          }
        />

        {/* Status + Priority badges */}
        <Stack direction="row" spacing={1.5} mb={3}>
          <Chip
            label={`Status: ${view.ticket.status}`}
            color={STATUS_COLORS[view.ticket.status] || "default"}
            size="small"
            sx={{ fontWeight: 700, textTransform: "capitalize" }}
          />
          <Chip
            label={`Priority: ${view.ticket.priority}`}
            color={PRIORITY_COLORS[view.ticket.priority] || "default"}
            size="small"
            sx={{ fontWeight: 700, textTransform: "capitalize" }}
          />
        </Stack>

        {/* Messages */}
        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              {view.messages.map((msg, i) => {
                const isAdmin = msg.sender_role === "admin";
                return (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      justifyContent: isAdmin ? "flex-end" : "flex-start",
                    }}
                  >
                    <Box sx={{ maxWidth: "78%" }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={0.5}
                        justifyContent={isAdmin ? "flex-end" : "flex-start"}
                      >
                        {!isAdmin && (
                          <Avatar
                            sx={{
                              width: 22,
                              height: 22,
                              bgcolor: theme.palette.primary.main,
                              fontSize: 10,
                            }}
                          >
                            U
                          </Avatar>
                        )}
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color={isAdmin ? "primary.main" : "text.secondary"}
                        >
                          {isAdmin ? "Support Team" : "You"}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          · {formatDate(msg.createdAt)}
                        </Typography>
                        {isAdmin && (
                          <Avatar
                            sx={{
                              width: 22,
                              height: 22,
                              bgcolor: theme.palette.warning.main,
                              fontSize: 10,
                            }}
                          >
                            S
                          </Avatar>
                        )}
                      </Stack>
                      <Paper
                        sx={{
                          p: 2,
                          borderRadius: isAdmin
                            ? "12px 4px 12px 12px"
                            : "4px 12px 12px 12px",
                          bgcolor: isAdmin
                            ? alpha(theme.palette.primary.main, 0.08)
                            : theme.palette.mode === "dark"
                              ? alpha(theme.palette.background.paper, 0.6)
                              : "#F9FAFB",
                          border: `1px solid ${
                            isAdmin
                              ? alpha(theme.palette.primary.main, 0.18)
                              : theme.palette.divider
                          }`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75 }}
                        >
                          {msg.message}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>

        {/* Reply box */}
        {view.ticket.status !== "closed" ? (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Reply
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems="flex-end"
              >
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder="Type your message..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.ctrlKey) sendReply();
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  onClick={sendReply}
                  disabled={replying || !replyText.trim()}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    py: 1.5,
                    minWidth: 120,
                  }}
                >
                  {replying ? "Sending..." : "Send"}
                </Button>
              </Stack>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ mt: 0.5, display: "block" }}
              >
                Ctrl+Enter to send
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Paper
            variant="outlined"
            sx={{ p: 2.5, borderRadius: 3, textAlign: "center" }}
          >
            <Typography color="text.secondary" variant="body2">
              This ticket is closed. Create a new ticket if you need further
              assistance.
            </Typography>
          </Paper>
        )}
      </Box>
    );
  }

  // ── Ticket List View ────────────────────────────────────────────────────
  return (
    <Box>
      <PageHeader
        title="Support Tickets"
        subtitle={`${tickets.length} total tickets`}
        icon={SupportAgentIcon}
        primaryAction={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            New Ticket
          </Button>
        }
      />

      {tickets.length === 0 ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <EmptyState
              icon={SupportAgentIcon}
              title="No support tickets yet"
              description="Having an issue? Create a support ticket and our team will help you."
              action={() => setCreateOpen(true)}
              actionLabel="Create Ticket"
            />
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {tickets.map((ticket) => (
            <Card
              key={ticket.uid}
              sx={{
                borderRadius: 2.5,
                cursor: "pointer",
                border: "1px solid",
                borderColor:
                  ticket.status === "open"
                    ? alpha(theme.palette.error.main, 0.2)
                    : ticket.status === "answered"
                      ? alpha(theme.palette.info.main, 0.2)
                      : theme.palette.divider,
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateX(4px)",
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
              onClick={() => openTicket(ticket)}
            >
              <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ sm: "center" }}
                  spacing={1.5}
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      mb={0.5}
                    >
                      <Typography variant="body1" fontWeight={700}>
                        {ticket.subject}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Typography variant="caption" color="text.disabled">
                        #{ticket.uid?.substring(0, 8)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Updated: {formatDate(ticket.updatedAt)}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={ticket.priority}
                      size="small"
                      color={PRIORITY_COLORS[ticket.priority] || "default"}
                      sx={{
                        fontWeight: 700,
                        height: 22,
                        fontSize: "0.68rem",
                        textTransform: "capitalize",
                      }}
                    />
                    <Chip
                      label={ticket.status}
                      size="small"
                      color={STATUS_COLORS[ticket.status] || "default"}
                      sx={{
                        fontWeight: 700,
                        height: 22,
                        fontSize: "0.68rem",
                        textTransform: "capitalize",
                      }}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* ── Create Ticket Dialog ── */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontWeight={700} variant="h6">
              Create Support Ticket
            </Typography>
            <IconButton size="small" onClick={() => setCreateOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <TextField
              fullWidth
              size="small"
              label="Subject *"
              placeholder="Brief description of your issue"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={form.priority}
                label="Priority"
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                <MenuItem value="low">🟢 Low</MenuItem>
                <MenuItem value="medium">🟡 Medium</MenuItem>
                <MenuItem value="high">🔴 High</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={5}
              label="Message *"
              placeholder="Describe your issue in detail..."
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createTicket}
            disabled={creating || !form.subject.trim() || !form.message.trim()}
            endIcon={<SendIcon />}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            {creating ? "Submitting..." : "Submit Ticket"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportTickets;
